import { backendApi } from "./backendApi.rtkq";

// Types for Terms of Service
export interface TermsOfService {
  id?: string;
  version: string;
  pdf_url_en: string;
  pdf_url_he: string;
  createdAt: Date;
  effectiveDate: Date;
  isActive: boolean;
}

export interface TosAcceptance {
  accepted: boolean;
  version: string;
  timestamp: Date;
  ipAddress?: string;
}

export interface UpdateUserTosRequest {
  version: string;
  ipAddress?: string;
}

export interface UserTosStatus {
  needsAcceptance: boolean;
  currentVersion?: string;
  userAcceptance?: TosAcceptance;
  latestTos?: {
    version: string;
    effectiveDate: Date;
  };
  reason?: string;
}

export interface CreateUserAccountWithTosParams {
  version: string;
  ipAddress?: string;
}

export interface UpdateUserTosAcceptanceParams {
  version: string;
  ipAddress?: string;
}

export interface UpsertTermsOfServiceParams {
  version: string;
  pdf_url_en?: string;
  pdf_url_he?: string;
  effectiveDate?: Date;
  isActive?: boolean;
}

// Helper to convert date strings in ToS responses
const convertTosDates = (tos: any): TermsOfService => ({
  ...tos,
  createdAt: tos.createdAt instanceof Date ? tos.createdAt : new Date(tos.createdAt),
  effectiveDate: tos.effectiveDate instanceof Date ? tos.effectiveDate : new Date(tos.effectiveDate),
});

export const legalApi = backendApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get the latest active Terms of Service
    getLatestTermsOfService: builder.query<TermsOfService | null, void>({
      query: () => "/tos/latest",
      transformResponse: (response: any) => {
        if (!response) return null;
        return convertTosDates(response);
      },
      providesTags: ["Legal", { type: "Legal", id: "latest" }],
    }),

    // Get Terms of Service by version
    getTermsOfServiceByVersion: builder.query<TermsOfService | null, string>({
      query: (version) => `/tos/version/${version}`,
      transformResponse: (response: any) => {
        if (!response) return null;
        return convertTosDates(response);
      },
      providesTags: (result, _error, version) => (result ? [{ type: "Legal", id: version }] : []),
    }),

    // Check user's ToS acceptance status
    checkUserTosStatus: builder.query<UserTosStatus, void>({
      query: () => "/tos/status",
      transformResponse: (response: any) => {
        if (!response) return { needsAcceptance: false, reason: "Failed to check status" };
        return {
          ...response,
          userAcceptance: response.userAcceptance
            ? {
                ...response.userAcceptance,
                timestamp: response.userAcceptance.timestamp instanceof Date
                  ? response.userAcceptance.timestamp
                  : new Date(response.userAcceptance.timestamp),
              }
            : undefined,
          latestTos: response.latestTos
            ? {
                ...response.latestTos,
                effectiveDate: response.latestTos.effectiveDate instanceof Date
                  ? response.latestTos.effectiveDate
                  : new Date(response.latestTos.effectiveDate),
              }
            : undefined,
        };
      },
      providesTags: ["Legal", { type: "Legal", id: "user-status" }],
    }),

    // Accept Terms of Service
    acceptTermsOfService: builder.mutation<{ success: boolean; version: string }, UpdateUserTosAcceptanceParams>({
      query: ({ version, ipAddress }) => ({
        url: "/tos/accept",
        method: "POST",
        body: { version, ipAddress },
      }),
      transformResponse: (_response: any, _meta, { version }) => ({ success: true, version }),
      invalidatesTags: ["Legal", { type: "Legal", id: "user-status" }],
    }),

    // Create user account with ToS acceptance (for new signups)
    createUserAccountWithTos: builder.mutation<{ success: boolean; accountId: string }, CreateUserAccountWithTosParams>({
      query: ({ version, ipAddress }) => ({
        url: "/users/register",
        method: "POST",
        body: { tosVersion: version, ipAddress },
      }),
      transformResponse: (response: any) => ({
        success: true,
        accountId: response.accountId || response.officeId || response.id,
      }),
      invalidatesTags: ["Legal", { type: "Legal", id: "user-status" }],
    }),

    // Update existing user's ToS acceptance (for existing users)
    updateUserTosAcceptance: builder.mutation<{ success: boolean; version: string }, UpdateUserTosAcceptanceParams>({
      query: ({ version, ipAddress }) => ({
        url: "/tos/accept",
        method: "POST",
        body: { version, ipAddress },
      }),
      transformResponse: (_response: any, _meta, { version }) => ({ success: true, version }),
      invalidatesTags: ["Legal", { type: "Legal", id: "user-status" }],
    }),

    // Admin: Upsert Terms of Service (super admin only)
    upsertTermsOfService: builder.mutation<TermsOfService, UpsertTermsOfServiceParams>({
      query: (params) => ({
        url: "/tos",
        method: "PUT",
        body: params,
      }),
      transformResponse: (response: any) => convertTosDates(response),
      invalidatesTags: ["Legal", { type: "Legal", id: "latest" }],
    }),

    // Admin: Get all Terms of Service versions (super admin only)
    getAllTermsOfServiceVersions: builder.query<TermsOfService[], void>({
      query: () => "/tos",
      transformResponse: (response: any) => {
        const versions = response.versions || response || [];
        return versions.map(convertTosDates);
      },
      providesTags: ["Legal"],
    }),
  }),
});

export const {
  useGetLatestTermsOfServiceQuery,
  useGetTermsOfServiceByVersionQuery,
  useCheckUserTosStatusQuery,
  useAcceptTermsOfServiceMutation,
  useCreateUserAccountWithTosMutation,
  useUpdateUserTosAcceptanceMutation,
  useUpsertTermsOfServiceMutation,
  useGetAllTermsOfServiceVersionsQuery,
} = legalApi;
